import * as yup from 'yup';

import { Card, CardBody, CardFooter, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { Countries, States } from '../common/Data';
import { PersonDto, PersonnelPersonStatus, PersonnelSettings, ProgramPersonnelDto } from '../common/Types';

import Button from './Button';
import React, { useCallback, useMemo, useState } from 'react';
import { personnelService } from '../services/Personnel';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Spinner } from 'reactstrap';

type Props = {
    person: ProgramPersonnelDto;
    onCancel: () => void;
    onSave: () => void;
};

// Form data interface with proper typing
interface PersonnelFormData {
    programPersonnelId: number;
    programId: number;
    personnelTypeId: number;
    peopleId: number;
    visible: boolean;
    satelliteId: number | null;
    people: {
        peopleId: number;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        address3?: string;
        address4?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
        phone?: string;
        extension?: string;
        fax?: string;
        title?: string;
        credentials?: string;
        salutations?: string;
        email?: string;
        createdBy?: string;
        dateCreated?: string;
        lastEdited?: string;
        editedBy?: string;
        active: boolean;
    };
    reportYear: number;
    personnelStatus: number;
    personnelType?: PersonnelSettings;
}

const PersonnelForm = (props: Props) => {
    const { person } = props;

    // Memoize validation schema
    const personnelSchema = useMemo(
        () =>
            yup.object<PersonnelFormData>({
                programPersonnelId: yup.number().required().default(person.programPersonnelId),
                programId: yup.number().required().default(person.programId),
                personnelTypeId: yup.number().required().default(person.personnelTypeId),
                peopleId: yup.number().required().default(person.peopleId),
                visible: yup.boolean().required().default(person.visible),
                satelliteId: yup.number().nullable().default(person.satelliteId),
                people: yup
                    .object({
                        peopleId: yup.number().required().default(0),
                        firstName: yup.string().optional().nullable().default(''),
                        lastName: yup.string().optional().nullable().default(''),
                        address: yup.string().optional().nullable().default(''),
                        address2: yup.string().optional().nullable().default(''),
                        address3: yup.string().optional().nullable().default(''),
                        address4: yup.string().optional().nullable().default(''),
                        city: yup.string().optional().nullable().default(''),
                        state: yup.string().optional().nullable().default(''),
                        zip: yup.string().optional().nullable().default(''),
                        country: yup.string().optional().nullable().default(''),
                        phone: yup.string().optional().nullable().default(''),
                        extension: yup.string().optional().nullable().default(''),
                        fax: yup.string().optional().nullable().default(''),
                        title: yup.string().optional().nullable().default(''),
                        credentials: yup.string().optional().nullable().default(''),
                        salutations: yup.string().optional().nullable().default(''),
                        email: yup.string().email('Invalid email format').optional().nullable().default(''),
                        createdBy: yup.string().optional().nullable().default(''),
                        dateCreated: yup.string().optional().nullable().default(''),
                        lastEdited: yup.string().optional().nullable().default(''),
                        editedBy: yup.string().optional().nullable().default(''),
                        active: yup.boolean().required().default(true),
                    })
                    .required(),
                reportYear: yup.number().required().default(person.reportYear),
                personnelStatus: yup
                    .number()
                    .required('Please indicate the current status of this person')
                    .typeError('Please indicate the current status of this person')
                    .notOneOf([PersonnelPersonStatus.unknown], 'Please indicate the current status of this person.')
                    .default(person.personnelStatus || 0),
                personnelType: yup.object<PersonnelSettings>().optional(),
            }),
        [person],
    );

    const {
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { isSubmitting, errors, isDirty },
    } = useForm<PersonnelFormData>({
        resolver: yupResolver(personnelSchema) as any,
        mode: 'onChange',
        defaultValues: {
            programPersonnelId: person.programPersonnelId,
            programId: person.programId,
            personnelTypeId: person.personnelTypeId,
            peopleId: person.peopleId,
            visible: person.visible,
            satelliteId: person.satelliteId,
            people: {
                peopleId: person.people?.peopleId || 0,
                firstName: person.people?.firstName || '',
                lastName: person.people?.lastName || '',
                address: person.people?.address || '',
                address2: person.people?.address2 || '',
                address3: person.people?.address3 || '',
                address4: person.people?.address4 || '',
                city: person.people?.city || '',
                state: person.people?.state || '',
                zip: person.people?.zip || '',
                country: person.people?.country || '',
                phone: person.people?.phone || '',
                extension: person.people?.extension || '',
                fax: person.people?.fax || '',
                title: person.people?.title || '',
                credentials: person.people?.credentials || '',
                salutations: person.people?.salutations || '',
                email: person.people?.email || '',
                createdBy: person.people?.createdBy || '',
                dateCreated: person.people?.dateCreated || '',
                lastEdited: person.people?.lastEdited || '',
                editedBy: person.people?.editedBy || '',
                active: person.people?.active ?? true,
            },
            reportYear: person.reportYear,
            personnelStatus: person.personnelStatus || 0,
            personnelType: person.personnelType,
        },
    });

    // State for email lookup
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [emailLookupMessage, setEmailLookupMessage] = useState<string | null>(null);

    // Watch email field to control other field states
    const emailValue = watch('people.email');
    const isEmailEntered = !!emailValue && emailValue.trim().length > 0;

    // Email lookup handler - auto-populate fields when email is entered
    const handleEmailBlur = useCallback(
        async (email: string) => {
            // Clear previous message
            setEmailLookupMessage(null);

            // Validate email format before lookup
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!email || !emailPattern.test(email)) {
                return;
            }

            setIsLookingUp(true);
            try {
                const foundPerson = await personnelService.getPersonByEmail(email);
                if (foundPerson && foundPerson.peopleId) {
                    // Populate contact fields only. The lookup hits the AMS database,
                    // whose PeopleIDs are unrelated to ARMS PeopleIDs — copying the ID
                    // into this ARMS payload links the wrong person (or violates the
                    // People FK). Leave peopleId untouched so ARMS creates its own row.
                    setValue('people.firstName', foundPerson.firstName || '', { shouldDirty: true });
                    setValue('people.lastName', foundPerson.lastName || '', { shouldDirty: true });
                    setValue('people.title', foundPerson.title || '', { shouldDirty: true });
                    setValue('people.salutations', foundPerson.salutations || '', { shouldDirty: true });
                    setValue('people.credentials', foundPerson.credentials || '', { shouldDirty: true });
                    setValue('people.phone', foundPerson.phone || '', { shouldDirty: true });
                    setValue('people.extension', foundPerson.extension || '', { shouldDirty: true });
                    setValue('people.fax', foundPerson.fax || '', { shouldDirty: true });
                    setValue('people.address', foundPerson.address || '', { shouldDirty: true });
                    setValue('people.address2', foundPerson.address2 || '', { shouldDirty: true });
                    setValue('people.city', foundPerson.city || '', { shouldDirty: true });
                    setValue('people.state', foundPerson.state || '', { shouldDirty: true });
                    setValue('people.zip', foundPerson.zip || '', { shouldDirty: true });
                    setValue('people.country', foundPerson.country || '', { shouldDirty: true });
                    setEmailLookupMessage(`Found: ${foundPerson.firstName} ${foundPerson.lastName}`);
                }
            } catch (error) {
                // Silently fail - user can still enter data manually
                console.log('Email lookup failed:', error);
            } finally {
                setIsLookingUp(false);
            }
        },
        [setValue],
    );

    const onSubmit = useCallback(
        async (values: PersonnelFormData): Promise<void> => {
            // Skip if no changes
            if (!isDirty) {
                props.onCancel();
                return;
            }

            const toastId = toast.info('Saving person...');

            try {
                // Convert form data back to ProgramPersonnelDto format
                const apiData: ProgramPersonnelDto = {
                    programPersonnelId: values.programPersonnelId,
                    programId: values.programId,
                    personnelTypeId: values.personnelTypeId,
                    peopleId: values.peopleId,
                    visible: values.visible,
                    satelliteId: values.satelliteId,
                    people: {
                        peopleId: values.people.peopleId,
                        firstName: values.people.firstName,
                        lastName: values.people.lastName,
                        address: values.people.address,
                        address2: values.people.address2,
                        address3: values.people.address3,
                        address4: values.people.address4,
                        city: values.people.city,
                        state: values.people.state,
                        zip: values.people.zip,
                        country: values.people.country,
                        phone: values.people.phone,
                        extension: values.people.extension,
                        fax: values.people.fax,
                        title: values.people.title,
                        credentials: values.people.credentials,
                        salutations: values.people.salutations,
                        email: values.people.email,
                        createdBy: values.people.createdBy,
                        dateCreated: values.people.dateCreated,
                        lastEdited: values.people.lastEdited,
                        editedBy: values.people.editedBy,
                        active: values.people.active,
                    },
                    reportYear: values.reportYear,
                    personnelStatus: values.personnelStatus,
                    personnelType: values.personnelType,
                };

                await personnelService.saveProgramPersonnel(values.programId, values.reportYear, [apiData]);

                toast.update(toastId, {
                    render: 'Person saved successfully',
                    type: 'success',
                    autoClose: 3000,
                });

                props.onSave();
            } catch (error) {
                console.error('Save error:', error);
                toast.update(toastId, {
                    render: 'Error saving person',
                    type: 'error',
                    autoClose: 5000,
                });
            }
        },
        [isDirty, props],
    );

    return (
        <Card className={'m-0'}>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <CardBody>
                    {person && person.people && (
                        <Row>
                            <Col md={7}>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>
                                                Email
                                                {isLookingUp && (
                                                    <Spinner size="sm" className="ms-2" />
                                                )}
                                            </Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.email"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="email"
                                                            bsSize="sm"
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                            onBlur={(e) => {
                                                                field.onBlur();
                                                                handleEmailBlur(e.target.value);
                                                            }}
                                                        />
                                                    )}
                                                />
                                                {errors.people?.email && (
                                                    <span className="text-danger">{errors.people.email.message}</span>
                                                )}
                                                {emailLookupMessage && (
                                                    <span className="text-success small">{emailLookupMessage}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Job Title</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.title"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.title && (
                                                    <span className="text-danger">{errors.people.title.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xl={3}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Salutations</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.salutations"
                                                    control={control}
                                                    rules={{ required: false }}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.salutations && (
                                                    <span className="text-danger">
                                                        {errors.people.salutations.message}
                                                    </span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xl={3}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>First Name</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.firstName"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.firstName && (
                                                    <span className="text-danger">
                                                        {errors.people.firstName.message}
                                                    </span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xl={3}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Last Name</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.lastName"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.lastName && (
                                                    <span className="text-danger">
                                                        {errors.people.lastName.message}
                                                    </span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xl={3}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Credentials</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.credentials"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.credentials && (
                                                    <span className="text-danger">
                                                        {errors.people.credentials.message}
                                                    </span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xl={4}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Phone</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.phone"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.phone && (
                                                    <span className="text-danger">{errors.people.phone.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xl={2}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Ext</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.extension"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.extension && (
                                                    <span className="text-danger">
                                                        {errors.people.extension.message}
                                                    </span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Fax</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.fax"
                                                    control={control}
                                                    rules={{ required: false }}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.fax && (
                                                    <span className="text-danger">{errors.people.fax.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={5}>
                                <Row>
                                    <Col xs={12}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Address</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.address"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.address && (
                                                    <span className="text-danger">{errors.people.address.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col xs={12}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Address 2</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.address2"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.address2 && (
                                                    <span className="text-danger">
                                                        {errors.people.address2.message}
                                                    </span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={4}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>City</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.city"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.city && (
                                                    <span className="text-danger">{errors.people.city.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={4}>
                                        <FormGroup className={'row'}>
                                            <Label title={'State / Province'} className={'col-form-label col-12'}>
                                                St. / Pro...
                                            </Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.state"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="select"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        >
                                                            <option value="">{``}</option>
                                                            {States &&
                                                                States.map((s) => (
                                                                    <option key={s.abbreviation} value={s.abbreviation}>
                                                                        {s.abbreviation}
                                                                    </option>
                                                                ))}
                                                        </Input>
                                                    )}
                                                />
                                                {errors.people?.state && (
                                                    <span className="text-danger">{errors.people.state.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={4}>
                                        <FormGroup className={'row'}>
                                            <Label title={'Zip / Postal Code'} className={'col-form-label col-12'}>
                                                Zip / Post...
                                            </Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.zip"
                                                    control={control}
                                                    rules={{ required: false }}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.people?.zip && (
                                                    <span className="text-danger">{errors.people.zip.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                    <Col xs={12}>
                                        <FormGroup className={'row'}>
                                            <Label className={'col-form-label col-12'}>Country</Label>
                                            <Col xs={12}>
                                                <Controller
                                                    name="people.country"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="select"
                                                            bsSize="sm"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(e.target.value || '')
                                                            }
                                                        >
                                                            <option value="">{``}</option>
                                                            {Countries &&
                                                                Countries.map((c) => (
                                                                    <option key={c} value={c}>
                                                                        {c}
                                                                    </option>
                                                                ))}
                                                        </Input>
                                                    )}
                                                />
                                                {errors.people?.country && (
                                                    <span className="text-danger">{errors.people.country.message}</span>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    )}
                </CardBody>
                <CardFooter>
                    <Button disabled={isSubmitting} type={'submit'} color={'primary'}>
                        {`Save`}
                    </Button>
                    <Button
                        type={'button'}
                        color={'secondary'}
                        className={'ms-2'}
                        onClick={() => (props.onCancel ? props.onCancel() : null)}
                    >
                        {`Cancel`}
                    </Button>
                </CardFooter>
            </Form>
        </Card>
    );
};

export default PersonnelForm;
